/**
 * useDocSearch — search the doc store via SL.
 *
 * SL endpoint shipped in PR-1045: `GET /v1/commander/docs/search?q=…&type=…&status=…&limit=…`
 * returns `{ data: DocSearchResult[], total: number }`. RBAC gate is
 * server-side commander-scope (FE adds an additional email allowlist
 * for commander+simon per Commander 2026-04-28 directive).
 *
 * Two query modes:
 *   - `useDocSearch({ q, type?, status?, limit? })` — fuzzy search by title/text.
 *   - `useDocLookupById(id)` — direct fetch by UUID. Lives in
 *     `./useDocSearch.body` alongside `useDocBody`; both surfaces share the
 *     `/v1/commander/docs/{id}` SL endpoint and `DocByIdResponseSchema`.
 *
 * Pillar 3 migration (2026-05-24): both surfaces go through `getTyped`
 * with Pillar 2 `DocSearchEnvelopeSchema` / `DocByIdResponseSchema` per
 * ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.2.
 *
 * Split (2026-05-24): the heavier wire-bound surfaces (`useDocBody`,
 * `useDocLookupById`) live in `./useDocSearch.body` so this module
 * stays under the guard-file-size 300-LOC ceiling. The `@appic/hooks`
 * barrel re-exports both modules so callers see a single import site.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { getTyped } from '@appic/lib/http/getTyped'
import { CommanderDocsSearchEnvelope as DocSearchEnvelopeSchema } from '@appic/lib/wire/generated/schemas.generated'
import { DATA_CLASS } from './dataClasses'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DocSearchResult {
  // `id` is the one fail-loud identity field (used to build the
  // `/atc/docs/{id}` route + copy action). Every other column is a lighter
  // search projection — optional + nullable per `CommanderDocSearchRow`
  // (BUG-2026-06-30-RJNTC). Consumers already null-guard each access.
  id: string
  type?: string | null
  title?: string | null
  status?: string | null
  version?: number | null
  updated_at?: string | null
  created_at?: string | null
  // Lifecycle / process fields are optional — surface what SL returns.
  lifecycle?: string | null
  process?: string | null
  // Optional snippet for display (SL may add later).
  snippet?: string | null
  [key: string]: unknown
}

export interface DocSearchResponse {
  data: DocSearchResult[]
  total: number
}

export interface DocSearchParams {
  q: string
  type?: string
  status?: string
  limit?: number
}

export function buildDocSearchParams({
  q,
  type,
  status,
  limit,
  offset,
}: DocSearchParams & { offset?: number }): URLSearchParams {
  const sp = new URLSearchParams()
  sp.set('q', q.trim())
  if (type) sp.set('type', type)
  if (status) sp.set('status', status)
  sp.set('limit', String(limit))
  if (typeof offset === 'number') sp.set('offset', String(offset))
  return sp
}

export function coerceDocSearchResponse(payload: unknown): DocSearchResponse {
  if (!payload || typeof payload !== 'object') return { data: [], total: 0 }
  const body = payload as { data?: unknown; total?: unknown }
  if (!Array.isArray(body.data)) return { data: [], total: 0 }
  return {
    data: body.data,
    total: typeof body.total === 'number' ? body.total : 0,
  }
}

export function getDocSearchNextOffset(
  lastPage: DocSearchResponse,
  _allPages: DocSearchResponse[],
  lastPageParam: number,
): number | undefined {
  // Pagination plumbing: next offset = previous offset + items in last page.
  // Avoids reduce-summation (guard-fe-no-business-logic flagged the prior
  // allPages.reduce). Plain addition is not business logic — it's the
  // cursor math the SL contract requires (limit/offset).
  const nextOffset = lastPageParam + lastPage.data.length
  return nextOffset < lastPage.total ? nextOffset : undefined
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isUuid(query: string): boolean {
  return UUID_REGEX.test(query.trim())
}

/**
 * Internal: search wire call shared by the list hooks here AND by the
 * by-id resolver in `./useDocSearch.body` (which falls back to title/slug
 * search when the token isn't a UUID). Exported for that sibling module
 * only — outside the hooks package, callers should go through the
 * `useDocSearch` / `useDocSearchInfinite` hooks instead.
 */
export async function fetchDocSearch(
  params: DocSearchParams & { offset?: number },
  signal?: AbortSignal,
): Promise<DocSearchResponse> {
  const sp = buildDocSearchParams(params)
  const envelope = await getTyped(
    `/v1/commander/docs/search?${sp.toString()}`,
    DocSearchEnvelopeSchema,
    { signal },
  )
  return {
    data: envelope.data as unknown as DocSearchResult[],
    total: envelope.total,
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useDocSearch(params: DocSearchParams) {
  const { q, type, status, limit = 50 } = params
  const enabled = q.trim().length > 0
  return useQuery({
    queryKey: ['docs-search', q, type ?? '', status ?? '', limit] as const,
    queryFn: ({ signal }): Promise<DocSearchResponse> =>
      fetchDocSearch({ q, type, status, limit }, signal),
    enabled,
    staleTime: DATA_CLASS.SESSION,
  })
}

export function useDocSearchInfinite(params: DocSearchParams) {
  const { q, type, status, limit = 100 } = params
  const enabled = q.trim().length > 0
  return useInfiniteQuery({
    queryKey: ['docs-search-infinite', q, type ?? '', status ?? '', limit] as const,
    initialPageParam: 0 as number,
    queryFn: ({ pageParam, signal }): Promise<DocSearchResponse> =>
      fetchDocSearch({ q, type, status, limit, offset: pageParam }, signal),
    getNextPageParam: getDocSearchNextOffset,
    enabled,
    staleTime: DATA_CLASS.SESSION,
  })
}
